import React, { useState, useEffect, createContext, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import _ from 'lodash';
import {
  Grid,
  Tabs,
  Tab,
  Typography,
  IconButton,
  LinearProgress,
  Box,
  Button,
} from '@material-ui/core';
import {
  Save as SaveIcon,
  MoreVert as MoreVertIcon,
} from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import { EDU_MODE } from 'utils/config';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';

import * as objectService from 'services/estimateObjects';
import * as projectService from 'services/projects';
import { toast, getQueryVariable, getCurrentAccount, canAccess as canAccessUtil } from 'utils/helpers';
import { Dashboard as DashboardLayout } from 'layouts';
import { FormField2 as Field, CommonPanel, MenuCustom, LicenceRequestForm } from 'components';
import WorkReportTable from '../WorkReport/WorkReportTable';
import WorkReportPercentageModal from './WorkReportPercentageModal';
import ResourceManager from './ResourceManager';
import ObjectSummaryModal from '../EstimateObjectForm/ObjectSummaryModal';
import ItemSummaryModal from '../EstimateObjectForm/ItemSummaryModal';
import PrintManager from './PrintManager';
import api from 'utils/api';

import '../EstimateObjectForm/EstimateObjectForm.css';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(4),
    // maxWidth: '1200px',
  },
  titleCoefficients: {
    fontSize: '9px',
    padding: '0px 8px 2px',
  },
}));

const TABS = {
  WORK_REPORTS: 0,
  MATERIALS: 1,
  MECHANISMS: 2,
  EQUIPMENT: 3,
  WORK: 4,
  PRINT: 5,
};

export const DETAIL_LEVEL = {
  ESTIMATE: 'estimate',
  PART: 'part',
  CHAPTER: 'chapter',
  SUBCHAPTER: 'subchapter',
  ELEMENT: 'element',
  GROUP: 'group',
  RATE: 'rate',
  RESOURCE: 'resource',
};

export const DETAIL_LEVEL_VISIBILITY = {
  [DETAIL_LEVEL.ESTIMATE]: [],
  [DETAIL_LEVEL.PART]: [DETAIL_LEVEL.ESTIMATE],
  [DETAIL_LEVEL.CHAPTER]: [DETAIL_LEVEL.ESTIMATE, DETAIL_LEVEL.PART],
  [DETAIL_LEVEL.SUBCHAPTER]: [DETAIL_LEVEL.ESTIMATE, DETAIL_LEVEL.PART, DETAIL_LEVEL.CHAPTER],
  [DETAIL_LEVEL.GROUP]: [DETAIL_LEVEL.ESTIMATE, DETAIL_LEVEL.PART, DETAIL_LEVEL.CHAPTER, DETAIL_LEVEL.SUBCHAPTER],
  [DETAIL_LEVEL.ELEMENT]: [DETAIL_LEVEL.ESTIMATE, DETAIL_LEVEL.PART, DETAIL_LEVEL.CHAPTER, DETAIL_LEVEL.SUBCHAPTER, DETAIL_LEVEL.GROUP],
  [DETAIL_LEVEL.RATE]: [DETAIL_LEVEL.ESTIMATE, DETAIL_LEVEL.PART, DETAIL_LEVEL.CHAPTER, DETAIL_LEVEL.SUBCHAPTER, DETAIL_LEVEL.ELEMENT, DETAIL_LEVEL.GROUP],
  [DETAIL_LEVEL.RESOURCE]: [DETAIL_LEVEL.ESTIMATE, DETAIL_LEVEL.PART, DETAIL_LEVEL.CHAPTER, DETAIL_LEVEL.SUBCHAPTER, DETAIL_LEVEL.ELEMENT, DETAIL_LEVEL.GROUP, DETAIL_LEVEL.RATE, DETAIL_LEVEL.RESOURCE],
};

export const TYPES = {
  ESTIMATE: 'estimate',
  PART: 'part',
  CHAPTER: 'chapter',
  SUBCHAPTER: 'subchapter',
  GROUP: 'group',
  ELEMENT: 'element',
  RATE: 'rate',
  RESOURCE: 'resource',
};

export const EstimateContext = createContext();
export const WorkReportContext = createContext();

const SERVICE_CODE = 'workReports';
const SERVICE_CODE_DB = 'SAM';
const SERVICE_RULE = 'services.work-reports';

const WorkReportForm = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const classes = useStyles();
  const { projectId, objectId, workReportId } = useParams();
  const [record, setRecord] = useState(null);
  const [project, setProject] = useState(null);
  const [config, setConfig] = useState(null);
  const [activeTab, setActiveTab] = useState(TABS.WORK_REPORTS);
  const [loading, setLoading] = useState(false);
  const [detailLevel, setDetailLevel] = useState(DETAIL_LEVEL.RESOURCE);
  const [idToHighlight, setIdToHighlight] = useState(null);
  const [treeExpandedRowKeys, setTreeExpandedRowKeys] = useState([]);
  const [treeDetailLevel, setTreeDetailLevel] = useState(DETAIL_LEVEL.ESTIMATE);
  const [showTrialMessage, setShowTrialMessage] = useState(getCurrentAccount().trialServices.includes('estimates'));
  const [isTrial, setIsTrial] = useState(getCurrentAccount().trialServices.includes('estimates'));
  const [canAccessService, setCanAccessService] = useState(false);
  const [estimateCurrentEditable, setEstimateCurrentEditable] = useState();
  const [itemAdded, setItemAdded] = useState(null);
  const [showPercentageModal, setShowPercentageModal] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [showObjectSummaryModal, setShowObjectSummaryModal] = useState(false);
  const [showItemSummaryModal, setShowItemSummaryModal] = useState(false);

  const fetchRecord = async () => {
    try {
      const { data } = await api.get(`/work-reports/${workReportId}`);

      setRecord(data);
    } catch (err) {
      console.error(err);
      toast(t('Klaida įkeliant įrašą'), 'error');
    }
  }


  const fetchConfig = async () => {
    try {
      const data = await objectService.getConfig();
      setConfig(data);
    } catch (err) {
      console.error(err);
      toast(t('Klaida įkeliant konfigūraciją'), 'error');
    }
  }

  const fetchProject = async (projectId) => {
    try {
      const data = await projectService.show(projectId);
      setProject(data);
    } catch (err) {
      console.error(err);
      toast(t('Klaida įkeliant įrašą'), 'error');
    }
  }

  const checkLicence = async () => {
    try {
      const response = await api.post(`/licences/licence-valid`, { service: SERVICE_CODE_DB, rule: SERVICE_RULE });
      setCanAccessService(response.data.validLicence);
      return response.data.validLicence;
    } catch (err) {
      console.error(err);
      toast(t('Klaida įkeliant konfigūraciją'), 'error');
    }
  }

  useEffect(() => {
    if (workReportId) {
      checkLicence().then(canAccess => {
      if (canAccess) fetchRecord(workReportId)
        .then(() => {
          fetchConfig()
          fetchProject(projectId);
        });
      });
    }
  }, []);

  const onSubmit = async (data = null) => {
    try {
      const payload = _.omit(data || record, 'items');
      payload.project = projectId;

      await fetchRecord(record._id);
      toast(t('Atnaujinta'), 'success');
    } catch (err) {
      console.error(err);
      toast(t('Nepavyko išsaugoti'), 'error');
    }
  };

  const onDelete = async () => {
    if (!window.confirm('Ar tikrai norite ištrinti visus skaičiavimo duomenis?')) return;

    try {
      await api.delete(`/work-reports/${record._id}`);
      toast(t('Pašalinta'), 'success');
      
      history.push({ 
        pathname: `/projects/${projectId}/objects/${record?.estimateObject}`,
        tab: `WORK_REPORT`
      });
    } catch (err) {
      console.error(err);
      toast(t('Nepavyko pašalinti'), 'error');
    }
  };

  const print = async (params) => {
    try {
      await api.post(`/work-reports/${record._id}/print`, params, { timeout: 1000 * 120 });

      toast(t('Dokumentas formuojamas'), 'info');
    } catch (err) {
      console.error(err);

      toast(t('Nepavyko suformuoti dokumento'), 'error');
    }
  }

  const itemActions = {
    cellChanged: async (field, item, value) => {
      const payload = {
        field,
        value,
      };

      try {
        await api.put(`/work-reports/${record._id}/items/${item._id}`, payload);
        await fetchRecord(record._id);
      } catch (err) {
        console.error(err);
      }
    },

    reportQuantityPercent: async (item, percentage) => {
      const payload = {
        percent: percentage,
      };

      try {
        await api.post(`/work-reports/${record._id}/items/${item._id}/report-percentage`, payload);
        await fetchRecord(record._id);
        setActiveItem(null);
      } catch (err) {
        console.error(err);
      }
    },

    reportFullQuantity: async (item) => {
      const payload = {
        selectedItemId: item._id,
      };

      try {
        await api.post(`/work-reports/${record._id}/items/${item._id}/report-full`, payload);
        await fetchRecord(record._id);
        setActiveItem(null);
      } catch (err) {
        console.error(err);
      }
    },

    showPriceComposition: async (item) => {
      try {
        const { data } = await api.get(`/work-reports/${record._id}/items/${item._id}/price-composition`);
        setActiveItem({ ...item, priceComposition: data });
        setShowItemSummaryModal(true);
      } catch (err) {
        console.error(err);
      }
    },
  };

  const renderModals = () => {
    if (!record?._id) return;

    const objectSummaryProps = {
      isEstimate: false,
      objectId: record._id,
      itemId: activeItem?._id,
      onChange: ({ field, value }) => {
        itemActions.cellChanged(field, activeItem, value);
      },
      onClose: () => {
        setShowObjectSummaryModal(false);
        setActiveItem(null);
      },
    };

    const itemSummaryProps = {
      isEstimate: false,
      objectId: record._id,
      itemId: activeItem?._id,
      onClose: () => {
        setShowItemSummaryModal(false);
        setActiveItem(null);
      },
    };

    return (
      <>
        {showObjectSummaryModal && <ObjectSummaryModal {...objectSummaryProps} />}
        {showItemSummaryModal && <ItemSummaryModal {...itemSummaryProps} />}
      </>
    );
  }

  // !!! this might prevent rerendering
  const workReportContextValue = useMemo(() => ({
    record,
    setRecord,
    project,
    setLoading,
    itemActions,
    config,
    setDetailLevel,
    detailLevel,
    onSubmit,
    // onRecalc,
    // onDelete,
    // onClone,
    treeExpandedRowKeys,
    setTreeExpandedRowKeys,
    treeDetailLevel,
    setTreeDetailLevel,
    estimateCurrentEditable,
    setEstimateCurrentEditable,
    itemAdded,
    setItemAdded,
    setShowPercentageModal,
    setActiveItem,
    setShowObjectSummaryModal,
    setShowItemSummaryModal,
    print,
  }),
    [
      record,
      setRecord,
      project,
      detailLevel,
      treeExpandedRowKeys,
      setTreeExpandedRowKeys,
      setTreeDetailLevel,
      estimateCurrentEditable,
      setEstimateCurrentEditable,
      itemAdded,
      setItemAdded,
      setShowPercentageModal,
      setActiveItem,
      setShowObjectSummaryModal,
      setShowItemSummaryModal,
      print,
    ]
  );

  const renderTrialMessage = () => {
    return EDU_MODE ? null : (
      <div style={{ marginLeft: 10, marginRight: 10, marginTop: 10, marginBottom: -10 }}>
        <Alert
          variant="filled"
          severity="info"
        >
          {t('Jūs naudojate bandomąją (laikinai nemokamą) paslaugos versiją.')}
        </Alert>
      </div>
    )
  }

  const renderLicenceRequest = () => {
    return (
      <LicenceRequestForm
        serviceCode={SERVICE_CODE}
      />
    )
  }

  const handleBackToEstimateObject = (toWorkReports = false) => {
    if (toWorkReports) {
      history.push({ 
        pathname: `/projects/${projectId}/objects/${record?.estimateObject}`,
        tab: `WORK_REPORT`
      });
    } else {
      history.push(`/projects/${projectId}/objects/${record?.estimateObject}`);
    }
  }
 
  const menuItems = [];
  menuItems.push({ label: t('Kainos sudėtis'), handler: () => setShowObjectSummaryModal(true) });
  menuItems.push({ label: t('Pašalinti'), handler: () => onDelete() });
  const isNew = false;

  let beforeMainContentMessage = [];

  if (isTrial && showTrialMessage) {
    beforeMainContentMessage.push(renderTrialMessage());
  }

  return (
    <>
      <DashboardLayout
        onBackClick={() => handleBackToEstimateObject()}
        isContentLoading={loading}
        title={t("Darbų aktas")}
        breadcrumbs={[
          { title: t('Projektai'), to: '/projects/' },
          { title: project?.title || '-', to: `/projects/${projectId}/dashboard` },
          { title: t('Objektai') },
          { title: `${(record?.title || '-')} ${record?.version?.version ? '(' + t('Kainynas ') + ' ' + record?.version?.version + ')' : ''}`, to: `/projects/${projectId}/objects/${record?.estimateObject}` },
          { title: t('Darbų aktai')},
          { title: `${record?.period}` }
        ]}
        breadcrumbsObject={!isNew && (
          <>
            <Button
              variant="contained"
              color="secondary"
              style={{ marginRight: 10 }}
              onClick={() => handleBackToEstimateObject(true)}
            >
              {t('Grįžti į darbų aktų sąrašą')}
            </Button>

            <Button
              variant="contained"
              color="secondary"
              style={{ marginRight: 10 }}
              onClick={() => handleBackToEstimateObject(false)}
            >
              {t('Grįžti į skaičiavimo objektą')}
            </Button>

            <MenuCustom
              buttonText={t('Veiksmai')}
              endIcon={<MoreVertIcon />}
              buttonVariant="contained"
              buttonColor="primary"
              items={menuItems || []}
            />
          </>
        )}
        beforeMainContent={beforeMainContentMessage}
      >
        <div className={classes.root} >
          <WorkReportContext.Provider value={workReportContextValue}>
            {canAccessService ? (
              <CommonPanel>
                
                <Grid item xs={12}>
                  <Tabs
                    value={activeTab}
                    onChange={(_e, newVal) => setActiveTab(newVal)}
                    scrollButtons="auto"
                    variant="scrollable"
                  >
                    <Tab label={t('Aktavimas')} disabled={!record?.version?.code} />
                    <Tab label={t('Medžiagos')} disabled={!record?.version?.code} />
                    <Tab label={t('Mechanizmai')} disabled={!record?.version?.code} />
                    <Tab label={t('Įrenginiai')} disabled={!record?.version?.code} />
                    <Tab label={t('Darbas')} disabled={!record?.version?.code} />
                    <Tab label={t('Spausdinimas')} disabled={!record?.version?.code}/>
                  </Tabs>
                </Grid>

                {!!record?.version?.code && (
                  <>
                    {activeTab === TABS.WORK_REPORTS && <WorkReportTable />}
                    {activeTab === TABS.MATERIALS && <ResourceManager resourceType="material" onReload={fetchRecord} />}
                    {activeTab === TABS.MECHANISMS && <ResourceManager resourceType="mechanism" onReload={fetchRecord} />}
                    {activeTab === TABS.EQUIPMENT && <ResourceManager resourceType="equipment" onReload={fetchRecord} />}
                    {activeTab === TABS.WORK && <ResourceManager resourceType="work" onReload={fetchRecord} />}
                    {activeTab === TABS.PRINT && <PrintManager isEstimate={false} />}
                  </>
                )}
                {/* {!isNew && !!config && } */}
                {showPercentageModal && 
                  <WorkReportPercentageModal
                    item={activeItem}
                    onClose={() => setShowPercentageModal(false)}
                  />
                }

              </CommonPanel>
            ) : renderLicenceRequest() }
          </WorkReportContext.Provider>
        </div>
      </DashboardLayout>

      {renderModals()}
    </>
  )
}

export default WorkReportForm;